import React, { useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import ModalTest from '../../BaseModal'
import * as S from '../../styles/modal-style'
import { X } from 'lucide-react'
import axios from '../../../config/axios'
import RenderField from '../RenderField'
import CommentsOfOp from '../../Modals/ViewPCP/Comments'
import { useNavigate, useSearchParams } from 'react-router-dom'

export interface IntegrationConfig {
  moduleId: string
  moduleName: string
  endpoint: string
  createEndpoint?: string
  fieldKey: string
  fieldName: string
  integrationType: 'read' | 'write'
  isRequired?: boolean
}

export interface FieldInTicket {
  ticketId?: number
  pipelineFieldId: number
  stringValue?: string
  bigStringValue?: string
  integerValue?: number
  decimalValue?: number
  dateTimeValue?: string
  booleanValue?: boolean
  pipelineField: {
    id: number
    name: string
    typeFieldId: number
    typeField?: {
      id: number
      name: string
    }
    integrationConfig?: IntegrationConfig
  }
  isRequired: boolean
}

export interface Ticket {
  id: number
  title: string
  deadline: Date
  urgency: boolean
  description: string
  stepId: number
  fieldInTickets: FieldInTicket[]
}

const ViewOrEditTicket:React.FC = () => {
  const [ searchParams ] = useSearchParams()
  const ticketId = searchParams.get('ticketId')
  const isFilled = searchParams.get('isFilled')
  const isFilledBoolean = isFilled == 'false' ? false : true
  const [ ticket, setTicket ] = useState<Ticket | null>(null)
  const navigate = useNavigate()
  
  const methods = useForm()
  const { control } = methods

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}/full`)
        if(response.data) {
          setTicket(response.data.data)
        }
      } catch(error) {
        console.error(error)
      }
    }

    fetchTicket()
  }, [])
  
  const handleSave = async () => {
    if(!ticket) return

    const errors: string[] = []
    const isEmail = (v: string) => /.+@.+\..+/.test(v)
    const onlyDigits = (v: string) => v.replace(/\D/g, '')
    const isCpfValid = (v: string) => onlyDigits(v).length === 11
    const isCnpjValid = (v: string) => onlyDigits(v).length === 14
    const isCelularValid = (v: string) => {
      const d = onlyDigits(v)
      return d.length >= 10 && d.length <= 11
    }
    const isTelefoneValid = (v: string) => onlyDigits(v).length === 10

    ticket.fieldInTickets.forEach((f) => {
      const typeName = f.pipelineField.typeField?.name || ''
      const hasIntegration = !!f.pipelineField.integrationConfig
      
      const isStringType = ['E-mail', 'CPF', 'CNPJ', 'Celular', 'Telefone', 'Texto simples'].includes(typeName)
      const isDateType = typeName === 'Data'
      const isCheckboxType = typeName === 'Checkbox'
      
      if (f.isRequired || f.pipelineField.integrationConfig?.isRequired) {
        if (hasIntegration && !f.integerValue) {
          errors.push(`Selecione um valor para ${f.pipelineField.name}`)
        } else if (!hasIntegration) {
          if (isStringType && !f.stringValue) errors.push(`Preencha o campo ${f.pipelineField.name}`)
          if (isDateType && !f.dateTimeValue) errors.push(`Preencha o campo ${f.pipelineField.name}`)
          if (isCheckboxType && f.booleanValue === undefined) errors.push(`Marque o campo ${f.pipelineField.name}`)
        }
      }

      if (typeName === 'E-mail' && f.stringValue && !isEmail(f.stringValue)) errors.push(`E-mail inválido em ${f.pipelineField.name}`)
      if (typeName === 'CPF' && f.stringValue && !isCpfValid(f.stringValue)) errors.push(`CPF inválido em ${f.pipelineField.name}`)
      if (typeName === 'CNPJ' && f.stringValue && !isCnpjValid(f.stringValue)) errors.push(`CNPJ inválido em ${f.pipelineField.name}`)
      if (typeName === 'Celular' && f.stringValue && !isCelularValid(f.stringValue)) errors.push(`Celular inválido em ${f.pipelineField.name}`)
      if (typeName === 'Telefone' && f.stringValue && !isTelefoneValid(f.stringValue)) errors.push(`Telefone inválido em ${f.pipelineField.name}`)
    })

    if (errors.length > 0) {
      alert(errors[0])
      return
    }

    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/${ticketId}`, {
        title: ticket.title,
        description: ticket.description,
        deadline: ticket.deadline,
        urgency: ticket.urgency,
        fieldInTickets: ticket.fieldInTickets
      })
      if(response.data) {
        navigate(-1)
      }
    } catch(error) {
      console.error(error)
    }
  }
  
  return (
    <FormProvider {...methods}>
      <ModalTest>
      <S.ContainerOfModal width='800px'>
        <S.Header>
          <S.HeaderTitle>{ticket?.title}</S.HeaderTitle>
          <S.CloseDiv onClick={() => navigate(-1)}> <X size={20} /></S.CloseDiv>
        </S.Header>

        <S.FormContainer columns='1fr 4fr'>
          <S.Label>Data de vencimento</S.Label>
          <S.Input type='date' value={ticket?.deadline ? new Date(ticket.deadline).toISOString().split('T')[0] : ''} onChange={(e) => setTicket({...ticket, deadline: new Date(e.target.value)} as Ticket)} />
          <S.Label>Urgente</S.Label>
          <S.HiddenCheckbox checked={ticket?.urgency || false} onChange={() => setTicket({...ticket, urgency: !ticket?.urgency} as Ticket)} />
          <S.StyledCheckbox onClick={() => setTicket({...ticket, urgency: !ticket?.urgency} as Ticket)} checked={ticket?.urgency || false} />
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          {ticket?.fieldInTickets.map((field) => (
            <RenderField field={field} setValue={setTicket as React.Dispatch<React.SetStateAction<Ticket>>} key={field.pipelineFieldId} isFilled={isFilledBoolean} control={control} />
          ))}
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <S.InputDiv style={{gridColumn: 'span 2'}}>
            <S.Label>Descrição</S.Label>
            <S.Textarea value={ticket?.description || ''} onChange={(e) => setTicket({...ticket, description: e.target.value} as Ticket)} placeholder='Digite aqui' />
          </S.InputDiv>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div style={{gridColumn: 'span 2'}}>
            <CommentsOfOp ticketId={ticket?.id || 0} />
          </div>
        </S.FormContainer>
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button onClick={() => navigate(-1)} className='cancel'>Cancelar</S.Button>
            <S.Button onClick={handleSave}>Salvar</S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </ModalTest>
    </FormProvider>
  )
}

export default ViewOrEditTicket